import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Brain, FilePlus, Home, BookOpen, LogOut, ArrowUpFromLine } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFirmContextStore } from '@/stores/useFirmContextStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { clearAuthToken } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { FirmContextSelector } from '@/components/common';

// Build-time flag — mirrors the flag checked in router.tsx.
const IMPORT_REVIEW_ENABLED = import.meta.env.VITE_IMPORT_REVIEW === 'true';

export function Header() {
  const location = useLocation();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const isKbAdmin =
    user?.permissions.some((p) => p === 'insight:admin' || p === 'insight:kb_admin') ?? false;

  const navItems = [
    { path: '/sessions', label: 'Home', icon: Home },
    { path: '/upload', label: 'New DDQ', icon: FilePlus },
    ...(isKbAdmin
      ? [{ path: '/knowledge-base', label: 'Knowledge Base', icon: BookOpen }]
      : []),
    ...(isKbAdmin && IMPORT_REVIEW_ENABLED
      ? [{ path: '/admin/import-review', label: 'Import Review', icon: ArrowUpFromLine }]
      : []),
  ];

  const handleLogout = () => {
    useSessionStore.getState().reset();
    useFirmContextStore.getState().reset();
    queryClient.clear();
    clearAuthToken();
    useAuthStore.getState().clear();
    window.location.href = '/login';
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container max-w-full flex h-14 items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mr-6">
          <Brain className="h-6 w-6 text-primary" />
          <span className="font-semibold text-lg">APX Respond</span>
        </Link>

        {/* Navigation */}
        <nav className="flex items-center gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* Spacer */}
        <div className="flex-1" />

        <FirmContextSelector />

        {/* User info + Logout */}
        {user && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{user.userName}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} title="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </header>
  );
}
